import { DB } from '../../../../typings/model/db';
import { GroupUserInfo, UserType } from '../../../../typings/proto';
import { ISimpleUser, TShowRole, TSimpleUserMap } from '../../../../typings/model/user';
import { RoleManager, RoleManagerUpdateType, TRoleMap } from '../../../../business/manager/role';
import { TGetUserMapAndRoleMapParam, TUpdateSimpleUserInfoMapParam } from '../../../../business/manager/group-user/typings';
import { SimpleUserManager } from '../index';
import { COMMON_CHANNEL_ID } from '../../../../constant/channel';

/**
 * 获取格式化的简要用户信息
 * @param groupChannelKey 群频道组合key值(groupId + channelId)
 * @param userInfo 简要用户信息
 * @returns 格式化后的简要用户信息 或 null值
 */
export function getFormatSimpleUser(groupChannelKey: string, userInfo?: DB.IUserInfo): ISimpleUser | null {
  if (!userInfo) {
    return null;
  }

  const { uid, avatar, userType, groupNicknameMap, showRoleIdMap } = userInfo;
  const groupNickname = groupNicknameMap?.[groupChannelKey];
  const showRoleId = showRoleIdMap?.[groupChannelKey];
  if (groupNickname && showRoleId) {
    return {
      uid,
      avatar,
      userType,
      groupNickname,
      showRole: {
        roleId: showRoleId,
      },
    };
  }
  return null;
}

/**
 * 获取格式化的dbUser
 * @param data 接口拉取回来的groupUserInfo
 * @param groupId 群id
 * @param groupChannelKey 群组频道的key
 * @returns 格式化后的dbUser
 */
export async function getFormatDBUser(
  data: GroupUserInfo,
  groupId: string,
  groupChannelKey: string,
): Promise<DB.IUserInfo> {
  const { userInfo, showNick } = data;
  const showRoleId = await getShowRoleId(data, groupId);
  const { uid = '', avatar = '', userType = UserType.UserTypeUnknown } = userInfo || {};

  return {
    uid,
    avatar,
    userType,
    groupNicknameMap: {
      [groupChannelKey]: showNick,
    },
    showRoleIdMap: {
      [groupChannelKey]: showRoleId,
    },
  };
}

/**
 * 获取用户的角色展示id
 * @param data 接口拉取回来的groupUserInfo
 * @param groupId 群id
 * @returns 角色展示id
 */
async function getShowRoleId(data: GroupUserInfo, groupId: string): Promise<string> {
  const { showRoleId, roleIdList } = data;
  const defaultRoleId = '0';
  // 1.用户指定了外显角色
  if (showRoleId && showRoleId !== defaultRoleId) {
    return showRoleId;
  }

  // 2.未指定，默认取第一个外显权限
  const roleMap = await RoleManager.getInstance().getRoleMapByGroupChannel(groupId);
  for (const roleId of roleIdList) {
    if (roleMap[roleId]?.isShow) {
      return roleId;
    }
  }
  return defaultRoleId;
}

async function getUserMapAndRoleMap(param: TGetUserMapAndRoleMapParam) {
  const { userIdList, groupId, channelId } = param;
  const { needUserForceUpdate = false, needRoleForceUpdate = false } = param;
  const userManager = SimpleUserManager.getInstance();
  const roleManager = RoleManager.getInstance();
  const userMap = await userManager.fetchSimpleUserMap(userIdList, groupId, channelId, needUserForceUpdate);
  const { updateImmediate, updateNormal } = RoleManagerUpdateType;
  const roleUpdateType = needRoleForceUpdate ? updateImmediate : updateNormal;
  const roleMap = await roleManager.getRoleMapByGroupChannel(groupId, COMMON_CHANNEL_ID, roleUpdateType);

  return {
    userMap,
    roleMap,
  };
}

/**
 * 获取最新的角色显示数据
 * @param originShowRole 原来的角色显示数据
 * @param roleMap 最新的角色数据Map
 */
function getNewShowRole(originShowRole: TShowRole, roleMap: TRoleMap): TShowRole {
  const role = roleMap[originShowRole.roleId ?? ''];
  if (!role) {
    return originShowRole;
  }

  const color = role.color ?? '';
  const disPlayLabel = role.display?.label || {};
  const showLabel = disPlayLabel.open ?? false;
  const labelText = disPlayLabel.text ?? '';
  return {
    ...originShowRole,
    color,
    showLabel,
    labelText,
    hasUpdate: true,
  };
}


/**
 * 获取简单用户信息Map
 * @param param 相关参数
 * @returns 简单用户信息Map
 */
export async function getSimpleUserInfoMap(param: TGetUserMapAndRoleMapParam): Promise<TSimpleUserMap> {
  const { userIdList } = param;
  const { userMap, roleMap } = await getUserMapAndRoleMap(param);
  const simpleUserInfoMap: TSimpleUserMap = {};
  for (const userId of userIdList) {
    const user = userMap[userId];
    if (!user?.showRole) {
      continue;
    }

    const showRole = getNewShowRole(user.showRole, roleMap);
    const formatUser = {
      ...user,
      showRole,
    };
    simpleUserInfoMap[userId] = formatUser;
  }
  return simpleUserInfoMap;
}

/**
 * 更新简单用户信息Map中，指定item的属性值
 * @param param 相关参数
 * @returns 简单用户信息Map
 */
export async function updateSimpleUserInfoMapItemProperty(
  param: TGetUserMapAndRoleMapParam,
  simpleUserInfoMap: TSimpleUserMap,
): Promise<TSimpleUserMap> {
  const { userIdList } = param;
  const { userMap, roleMap } = await getUserMapAndRoleMap(param);
  for (const userId of userIdList) {
    const user = userMap[userId];
    if (!user?.showRole) {
      continue;
    }
    const { avatar, groupNickname, userType } = user;
    const newShowRole = getNewShowRole(user.showRole, roleMap);
    const cacheUser = simpleUserInfoMap[userId];
    if (cacheUser) {
      cacheUser.uid = userId;
      cacheUser.avatar = avatar;
      cacheUser.showRole = newShowRole;
      cacheUser.userType = userType;
      cacheUser.groupNickname = groupNickname;
    }
  }
  return simpleUserInfoMap;
}

/**
 * TODO：慎用，后续会使用updateSimpleUserInfoMapItemProperty替换
 */
export async function updateSimpleUserInfoMap(param: TUpdateSimpleUserInfoMapParam): Promise<TSimpleUserMap> {
  const { userIdList, groupId, channelId, needForceUpdate = false, simpleUserInfoMap } = param;
  const updateType = needForceUpdate ? RoleManagerUpdateType.updateImmediate : RoleManagerUpdateType.updateNormal;
  const userManager = SimpleUserManager.getInstance();
  const userMap = await userManager.fetchSimpleUserMap(userIdList, groupId, channelId, needForceUpdate);
  const roleMap = await RoleManager.getInstance().getRoleMapByGroupChannel(groupId, channelId, updateType);
  const result = simpleUserInfoMap || {};
  for (const userId of userIdList) {
    const user = userMap[userId];
    if (!user?.showRole) {
      continue;
    }
    const { avatar, groupNickname } = user;
    const roleColor = roleMap[user.showRole.roleId || '']?.color || '';
    const labelText = roleMap[user.showRole.roleId || '']?.display?.label?.text || '';
    const showLabel = roleMap[user.showRole.roleId || '']?.display?.label?.open || false;
    const cacheUser = result[userId];
    if (!cacheUser?.showRole) {
      user.showRole.color = roleColor;
      user.showRole.labelText = labelText;
      user.showRole.showLabel = showLabel;
      result[userId] = user;
    } else {
      cacheUser.avatar = avatar;
      cacheUser.groupNickname = groupNickname;
      cacheUser.showRole.color = roleColor;
      cacheUser.showRole.labelText = labelText;
      cacheUser.showRole.showLabel = showLabel;
    }
  }
  return result;
}
